import { useMemo, useState } from 'react'
import type { RawNomination } from './awards'
import { useTmdbLazyMovie } from '../library/useTmdbLazy'
import { LazyImage } from '../ui'
import { WatchlistPicker } from '../watchlists/WatchlistPicker'
import { useWatchlists } from '../watchlists/useWatchlists'

/**
 * Pull the relative path out of a hydrated TMDB poster URL so the
 * watchlist write matches the same `posterPath` shape used elsewhere
 * (see `MovieDetailModal.tmdbRelativePath`). The PWA stores relative
 * paths in Firestore so the desktop can rebuild the URL with whatever
 * size suffix it prefers.
 */
function relativePosterPath(url: string | null | undefined): string | null {
  if (!url) return null
  const m = url.match(/\/t\/p\/w\d+(\/.+)$/)
  return m ? m[1] : null
}

interface Props {
  nom: RawNomination
  isWinner: boolean
  inLibrary: boolean
  onOpen: () => void
}

/**
 * Single nominee tile with poster, title, year and winner/library
 * badges. Ports desktop's movie-centric SearchResultCard style so the
 * grid keeps the same visual weight as the library grid.
 *
 * The poster is fetched lazily through useTmdbLazyMovie — the hook uses
 * an IntersectionObserver so scrolling through a 20-category edition
 * doesn't fire 200 TMDB lookups at once.
 */
export function NomineeCard({ nom, isWinner, inLibrary, onOpen }: Props) {
  const hasTmdb = nom.movieTmdbId != null
  const lazy = useTmdbLazyMovie(hasTmdb ? nom.movieTmdbId! : 0)
  const tmdb = hasTmdb ? lazy.details : undefined
  const title = tmdb?.title ?? nom.movieTitle ?? nom.personName ?? 'Unknown'
  const year = tmdb?.year
  const poster = tmdb?.posterUrl
  const rating = tmdb?.rating ?? 0

  // Watchlist save state. We piggy-back on `useWatchlists` — the same
  // Firestore subscription used by every other watchlist surface — so
  // the bookmark icon stays in sync the moment the user toggles a list
  // in the picker (or anywhere else in the app).
  const { watchlists } = useWatchlists()
  const inAnyWatchlist = useMemo(() => {
    if (!hasTmdb) return false
    return watchlists.some((wl) =>
      wl.movies.some((m) => m.tmdbId === nom.movieTmdbId)
    )
  }, [watchlists, hasTmdb, nom.movieTmdbId])

  const [pickerOpen, setPickerOpen] = useState(false)

  // We can't nest a <button> for the bookmark inside the <button> that
  // wraps the whole card, so the outer element is a <div> and the main
  // click target is an inner button. The bookmark sits as a sibling
  // overlay so taps on it never bubble into onOpen.
  return (
    <div
      ref={hasTmdb ? (lazy.ref as (el: HTMLDivElement | null) => void) : undefined}
      className={`group relative flex w-full flex-col overflow-hidden rounded-xl border text-left transition-all duration-200 motion-safe:hover:-translate-y-0.5 ${
        isWinner
          ? 'border-accent-gold/50 bg-accent-gold/5 shadow-[0_0_0_1px_rgba(234,179,8,0.25)]'
          : 'border-border-primary bg-bg-secondary hover:border-accent-primary/40'
      }`}
    >
      <button
        type="button"
        onClick={onOpen}
        className="flex w-full flex-col text-left"
      >
        <div className="relative aspect-[2/3] w-full overflow-hidden bg-bg-tertiary">
          {poster ? (
            <LazyImage
              src={poster}
              alt={title}
              className="h-full w-full transition-transform duration-300 motion-safe:group-hover:scale-[1.03]"
              placeholderColor="#1a1a1a"
              fallback={
                <div className="flex h-full w-full items-center justify-center px-2 text-center text-[10px] text-text-tertiary">
                  {title.slice(0, 40)}
                </div>
              }
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center px-2 text-center text-[10px] text-text-tertiary">
              {title.slice(0, 40)}
            </div>
          )}

          {/* Winner badge (gold) */}
          {isWinner && (
            <div className="absolute left-2 top-2 flex items-center gap-1 rounded bg-accent-gold/80 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-black shadow-sm backdrop-blur-sm">
              <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                <path d="M12 2l2.39 4.84 5.34.78-3.87 3.77.91 5.32L12 14.2l-4.77 2.51.91-5.32L4.27 7.62l5.34-.78L12 2z" />
              </svg>
              Winner
            </div>
          )}

          {/* In-library badge (blue) */}
          {inLibrary && !isWinner && (
            <div className="absolute left-2 top-2 rounded bg-blue-500/80 px-1.5 py-0.5 text-[10px] font-semibold text-white shadow-sm backdrop-blur-sm">
              In library
            </div>
          )}

          {/* TMDB rating (blue pill, lower-right) */}
          {rating > 0 && (
            <div className="absolute bottom-2 right-2 rounded bg-blue-500/80 px-1.5 py-0.5 text-[10px] font-semibold text-white shadow-sm backdrop-blur-sm">
              {rating.toFixed(1)}
            </div>
          )}
        </div>

        <div className="flex min-h-[3rem] flex-1 flex-col justify-start p-2">
          <p className="line-clamp-2 text-[12px] font-semibold leading-tight text-text-primary">
            {title}
          </p>
          {year != null && (
            <p className="mt-0.5 text-[11px] font-medium text-accent-gold">{year}</p>
          )}
          {nom.personName && (
            <p className="mt-0.5 line-clamp-2 text-[10px] text-text-tertiary">
              {nom.personName}
            </p>
          )}
        </div>
      </button>

      {/* Save-to-watchlist bookmark overlay. Disabled when the nominee
          has no resolved TMDB id — without one we have no stable key to
          attach to the watchlist write, so we surface a tooltip
          explaining why instead of triggering a metadata fetch (which
          the awards data pipeline owns separately). */}
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation()
          if (!hasTmdb) return
          setPickerOpen(true)
        }}
        disabled={!hasTmdb}
        title={
          hasTmdb
            ? inAnyWatchlist
              ? 'In a watchlist — tap to manage'
              : 'Save to watchlist'
            : 'TMDB metadata not yet resolved for this nominee'
        }
        aria-label={
          hasTmdb
            ? inAnyWatchlist
              ? `${title} is in a watchlist — tap to manage`
              : `Save ${title} to a watchlist`
            : `Watchlist unavailable: TMDB metadata not yet resolved for ${title}`
        }
        className={`absolute right-2 top-2 z-20 inline-flex items-center justify-center rounded-md p-1.5 shadow-lg backdrop-blur-sm transition-colors ${
          inAnyWatchlist
            ? 'bg-green-500/90 text-white hover:bg-green-600'
            : 'border border-white/20 bg-black/50 text-white hover:bg-accent-primary'
        } ${!hasTmdb ? 'cursor-not-allowed opacity-40' : ''}`}
      >
        <svg
          className="h-4 w-4"
          viewBox="0 0 24 24"
          fill={inAnyWatchlist ? 'currentColor' : 'none'}
          stroke="currentColor"
          strokeWidth={2}
          aria-hidden
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-4-7 4V5z"
          />
        </svg>
      </button>

      {hasTmdb && (
        <WatchlistPicker
          open={pickerOpen}
          onClose={() => setPickerOpen(false)}
          movie={{
            tmdbId: nom.movieTmdbId!,
            title: tmdb?.title ?? nom.movieTitle ?? title,
            year: tmdb?.year ?? null,
            posterPath: relativePosterPath(tmdb?.posterUrl),
          }}
        />
      )}
    </div>
  )
}
